/**
 * Quiz expiration utilities.
 * Parses API expirationAt (DD/MM/YYYY H:mm م/ص) in Egypt time (UTC+2),
 * and provides Ramadan default: next day at 3pm Egyptian time.
 */

const EGYPT_UTC_OFFSET_HOURS = 2;

/**
 * Parse API expiration string in Egypt time.
 * Format: "DD/MM/YYYY H:mm م" or "DD/MM/YYYY H:mm ص" (م = PM, ص = AM).
 * @param {string} expirationAt - e.g. "05/03/2026 4:00 م"
 * @returns {number|null} Expiry timestamp in ms, or null if unparseable
 */
export function parseExpirationAtMs(expirationAt) {
  if (!expirationAt || typeof expirationAt !== 'string') return null;
  const trimmed = expirationAt.trim();
  if (!trimmed) return null;

  const parts = trimmed.split(/\s+/);
  if (parts.length < 3) return null;

  const dateStr = parts[0];   // DD/MM/YYYY
  const timeStr = parts[1];    // H:mm
  const ampm = parts[2];      // م or ص

  const dateMatch = dateStr.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!dateMatch) return null;
  const [, d, m, y] = dateMatch;
  const day = parseInt(d, 10);
  const month = parseInt(m, 10) - 1;
  const year = parseInt(y, 10);

  const timeMatch = timeStr.match(/^(\d{1,2}):(\d{2})$/);
  if (!timeMatch) return null;
  let hour = parseInt(timeMatch[1], 10);
  const minute = parseInt(timeMatch[2], 10);

  if (ampm === 'م') {
    if (hour !== 12) hour += 12;
  } else if (ampm === 'ص') {
    if (hour === 12) hour = 0;
  } else {
    return null;
  }

  if (month < 0 || month > 11 || day < 1 || day > 31 || hour < 0 || hour > 23 || minute < 0 || minute > 59) {
    return null;
  }

  const utcHour = hour - EGYPT_UTC_OFFSET_HOURS;
  const ms = Date.UTC(year, month, day, utcHour, minute, 0, 0);
  if (Number.isNaN(ms)) return null;
  return ms;
}

/**
 * Ramadan default: from createdAt (ISO), next calendar day at 3pm Egyptian time (15:00 Egypt = 13:00 UTC).
 * @param {string} createdAt - ISO date string
 * @returns {number|null} Expiry timestamp in ms, or null if createdAt invalid
 */
export function getRamadanDefaultExpiryMs(createdAt) {
  if (!createdAt) return null;
  const createdMs = new Date(createdAt).getTime();
  if (Number.isNaN(createdMs)) return null;

  const nextDay = new Date(createdMs + 24 * 60 * 60 * 1000);
  const y = nextDay.getUTCFullYear();
  const m = nextDay.getUTCMonth();
  const d = nextDay.getUTCDate();
  return Date.UTC(y, m, d, 13, 0, 0, 0);
}

/**
 * Format remaining time for countdown display.
 * When total hours >= 24, shows days then hours:minutes:seconds (e.g. "14 يوم 06:09:42 متبقي").
 * Otherwise shows HH:MM:SS or MM:SS.
 * @param {number} ms - Remaining milliseconds
 * @returns {string|null} Formatted string with " متبقي" suffix, or null if ms invalid/<=0
 */
export function formatRemainingTime(ms) {
  if (ms == null || ms <= 0) return null;
  const totalSeconds = Math.floor(ms / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const remainderSeconds = totalSeconds % 86400;
  const hours = Math.floor(remainderSeconds / 3600);
  const minutes = Math.floor((remainderSeconds % 3600) / 60);
  const seconds = remainderSeconds % 60;
  const hh = String(hours).padStart(2, '0');
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  const clock = `${hh}:${mm}:${ss}`;
  if (days > 0) {
    return `متبقي ${days}:${clock} يوم`;
  }
  if (hours > 0) {
    return `متبقي ${clock} ساعة`;
  }
  return `متبقي ${mm}:${ss} دقيقة`;
}

/**
 * Single source of truth for quiz expiry timestamp.
 * - If quiz.expirationAt is present and parseable → use it.
 * - Else if quiz.ramadan and quiz.createdAt → next day 3pm Egypt.
 * - Else → null (open, no countdown).
 * @param {{ expirationAt?: string | null, ramadan?: boolean, createdAt?: string | null }} quiz
 * @returns {number|null}
 */
export function getQuizExpiryMs(quiz) {
  if (!quiz) return null;

  const parsed = parseExpirationAtMs(quiz.expirationAt);
  if (parsed != null) return parsed;

  if (quiz.ramadan && quiz.createdAt) {
    return getRamadanDefaultExpiryMs(quiz.createdAt);
  }

  return null;
}
